import {
  Checkbox,
  Field,
  RadioCard,
  Text,
} from '@noorden-insurtech/alba-design-system';
import {
  brlInputToCents,
  centsToBRL,
} from '@/features/quotes/utils/rd-units.util';
import {
  BASICA_REQUIRED_MESSAGE,
  DESPESA_SALVAMENTO_SLUG,
  OVER_LIMIT_MESSAGE,
  type CoverageRow,
} from './coverage-rows.util';
import type { RdIndemnityPeriod } from '@/services/quotes/rd-equipamentos/types';

type CoverageRowsTableProps = {
  rows: CoverageRow[];
  /** Erro por linha, indexado pelo slug da cobertura (ver `resolveCoverageFieldRowIndex`). */
  rowErrors?: Record<string, string>;
  /** Linha `mandatory` com LMI 0/vazio — exibe `BASICA_REQUIRED_MESSAGE` na linha da Básica. */
  basicaInvalid?: boolean;
  disabled?: boolean;
  // eslint-disable-next-line no-unused-vars -- nome dos parâmetros documenta o contrato da callback
  onToggle: (slug: string) => void;
  // eslint-disable-next-line no-unused-vars -- idem
  onLmiChange: (slug: string, raw: string) => void;
  /** Prazos oferecíveis, do cadastro do motor — vazio enquanto não chegam. */
  indemnityPeriods?: RdIndemnityPeriod[];
  // eslint-disable-next-line no-unused-vars -- idem
  onIndemnityChange: (slug: string, months: number) => void;
};

/**
 * Tabela de coberturas de um equipamento — componente CONTROLADO e sem
 * efeitos: uma linha por cobertura disponível na modalidade do item —
 * Cobertura (`Checkbox`; Básica pré-marcada via `coverageDef.mandatory`) e
 * LMI (moeda, cents). A Taxa NÃO é exibida ao corretor (spec de 2026-07-19).
 *
 * Usada nos dois pontos de edição do Step 2: dentro do formulário "Adicionar
 * equipamento" (estado local, ainda sem item no backend) e dentro do card de
 * cada item já adicionado, onde `CoverageMatrix` a envolve com autosave.
 */
export function CoverageRowsTable({
  rows,
  rowErrors = {},
  basicaInvalid = false,
  disabled = false,
  onToggle,
  onLmiChange,
  indemnityPeriods = [],
  onIndemnityChange,
}: CoverageRowsTableProps) {
  return (
    /*
      Duas geometrias, e a de baixo de `sm` é a que corrige um defeito medido:
      com a grid de duas colunas fixas (`min-w-160` = 640px) a 375px a coluna
      de LMI ficava INTEIRA fora da janela de scroll (janela de ~251px) — a
      matriz abria mostrando só nomes de cobertura, e nenhum campo de valor.
      Rolar o contêiner revelava, mas ninguém adivinha que há o que rolar.

      Abaixo de `sm` a linha empilha (nome em cima, LMI abaixo, largura
      total) e não há scroll horizontal nenhum. De `sm` para cima volta a
      tabela de duas colunas, com `overflow-x-auto` como rede de segurança —
      é o mesmo padrão de "tabela larga scrollável" de
      `group-life/step-risk.component.tsx`.

      A margem negativa `-mx-5` saiu junto: ela existia só para devolver à
      área de scroll o padding do Accordion do design system, e sem scroll a
      375px ela passaria a fazer a tabela vazar para fora do card.
    */
    <div className='overflow-x-auto rounded-xl border border-alba-gray-300'>
      <div className='divide-y divide-alba-gray-300 sm:min-w-160'>
        <div className='hidden grid-cols-[1fr_220px] gap-4 px-4 py-3 sm:grid'>
          <Text className='text-xs font-bold text-alba-black-500'>
            Cobertura
          </Text>
          <Text className='text-xs font-bold text-alba-black-500'>LMI</Text>
        </div>
        {rows.map(row => {
          const isDespesaSalvamento =
            row.coverageSlug === DESPESA_SALVAMENTO_SLUG;
          // C4: erro de validação da Básica só se sobrepõe quando o
          // backend não retornou um erro mais específico para a mesma
          // linha (rowErrors é reiniciado a cada tentativa de save).
          const basicaMissing = row.mandatory && basicaInvalid;
          // Teto estourado é checado no cliente para o corretor ver o erro
          // enquanto digita; o motor revalida e o erro dele (rowErrors) tem
          // precedência, por ser o que de fato barrou a gravação.
          const overLimit =
            row.selected &&
            row.maxCents !== null &&
            brlInputToCents(row.lmiDisplay) > row.maxCents;
          const rowError =
            rowErrors[row.coverageSlug] ??
            (basicaMissing ? BASICA_REQUIRED_MESSAGE : undefined) ??
            (overLimit ? OVER_LIMIT_MESSAGE : undefined);
          return (
            <div
              key={row.coverageSlug}
              className='grid grid-cols-1 items-start gap-2 px-4 py-3 sm:grid-cols-[1fr_220px] sm:gap-4'
            >
              <Checkbox.Root
                disabled={disabled || row.mandatory || row.derived}
                checked={row.selected}
                onCheckedChange={() => {}}
                onClick={() => !disabled && onToggle(row.coverageSlug)}
              >
                <Checkbox.Control>
                  <Checkbox.Indicator />
                </Checkbox.Control>
                <Checkbox.Label>
                  <span className='flex flex-wrap items-center gap-2'>
                    <span className='text-sm font-bold text-alba-black-500'>
                      {row.name}
                    </span>
                    {row.mandatory && (
                      <span className='inline-flex items-center justify-center gap-2 rounded-xl bg-[rgba(44,40,100,0.2)] px-2 py-0.5'>
                        <span className='text-xs font-bold text-alba-blue-500'>
                          Básica
                        </span>
                      </span>
                    )}
                  </span>
                </Checkbox.Label>
              </Checkbox.Root>

              {/* Período indenitário: a DURAÇÃO da indenização, escolhida
                  entre os prazos do cadastro. Não confundir com a franquia
                  temporal, que é a CARÊNCIA — o tempo antes de a indenização
                  começar; as duas saem no mesmo documento.

                  As três opções ficam à VISTA (PO 2026-09-04), e vivem na
                  coluna 1, sob o nome da cobertura: a geometria de duas
                  colunas desta linha corrige um defeito medido a 375px, e uma
                  terceira coluna o reabriria. `RadioCard` é o mesmo controle
                  do "Primeira parcela" no passo Parâmetros. */}
              {row.usesIndemnityPeriod && row.selected ? (
                <div className='flex flex-col gap-1 sm:col-start-1'>
                  <Text className='text-xs font-bold text-alba-black-500'>
                    Período indenitário
                    <span className='text-alba-error-red-500'>*</span>
                  </Text>
                  <RadioCard.Root
                    value={
                      row.indemnityMonths ? String(row.indemnityMonths) : ''
                    }
                    onValueChange={details =>
                      details.value &&
                      onIndemnityChange(row.coverageSlug, Number(details.value))
                    }
                  >
                    <div className='flex flex-wrap gap-2'>
                      {indemnityPeriods.map(period => (
                        <RadioCard.Item
                          key={period.months}
                          value={String(period.months)}
                          disabled={disabled}
                        >
                          <RadioCard.ItemControl />
                          <RadioCard.ItemText>{period.name}</RadioCard.ItemText>
                          <RadioCard.ItemHiddenInput />
                        </RadioCard.Item>
                      ))}
                    </div>
                  </RadioCard.Root>
                </div>
              ) : null}

              <Field.Root invalid={!!rowError}>
                {/* O cabeçalho da tabela some no empilhado — sem este rótulo
                    o campo ficaria sem nome visível abaixo de `sm`. */}
                <Field.Label className='text-xs font-bold text-alba-black-500 sm:hidden'>
                  LMI
                </Field.Label>
                <Field.Input
                  type='text'
                  inputMode='decimal'
                  aria-label={`I.S. ${row.name}`}
                  value={row.lmiDisplay}
                  onChange={e => onLmiChange(row.coverageSlug, e.target.value)}
                  placeholder='R$ 0,00'
                  disabled={disabled}
                  readOnly={row.mandatory || row.derived}
                />
                {rowError ? (
                  <Field.ErrorText>{rowError}</Field.ErrorText>
                ) : row.mandatory ? (
                  <Field.HelperText>
                    {row.basicaHint ?? 'igual ao valor do equipamento'}
                  </Field.HelperText>
                ) : isDespesaSalvamento ? (
                  <Field.HelperText>10% da Básica (fixo)</Field.HelperText>
                ) : row.maxCents !== null ? (
                  <Field.HelperText>
                    máximo {centsToBRL(row.maxCents)}
                  </Field.HelperText>
                ) : null}
              </Field.Root>
            </div>
          );
        })}
      </div>
    </div>
  );
}
